import React from 'react';
import { Col, Form, Row, TimePicker, Typography } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import CustomSelect from '../../../components/CustomSelect';
import { getFormattedUserTimeZone } from '../../../common/utils/utils';

const { Text } = Typography;

export type FrequencyType =
  'REALTIME' | 'ONCE_A_DAY' | 'TWICE_A_DAY' | 'WEEKLY' | null;

interface FrequencySelectorHorizontalProps {
  selectedFrequency: FrequencyType;
  setSelectedFrequency: (frequency: FrequencyType) => void;
  isPerShipment?: boolean;
}

/**
 * Frequency selector component for notification settings
 *
 * @param selectedFrequency - Currently selected frequency option
 * @param setSelectedFrequency - Callback to update selected frequency
 * @param isPerShipment - Whether selector is for per-shipment notifications (excludes REALTIME option)
 */
const FrequencySelectorHorizontal: React.FC<
  FrequencySelectorHorizontalProps
> = ({ selectedFrequency, setSelectedFrequency, isPerShipment = false }) => {
  const frequencyOptions = [
    { value: 'REALTIME', label: 'Real Time' },
    { value: 'ONCE_A_DAY', label: 'Once a day' },
    { value: 'TWICE_A_DAY', label: 'Twice a day' },
    { value: 'WEEKLY', label: 'Weekly' },
  ];

  const getFrequencyOptions = (isPerShipment: boolean) => {
    return isPerShipment ? frequencyOptions.slice(1) : frequencyOptions;
  };

  const dayOptions = [
    { value: 'Sunday', label: 'Sun' },
    { value: 'Monday', label: 'Mon' },
    { value: 'Tuesday', label: 'Tue' },
    { value: 'Wednesday', label: 'Wed' },
    { value: 'Thursday', label: 'Thu' },
    { value: 'Friday', label: 'Fri' },
    { value: 'Saturday', label: 'Sat' },
  ];

  return (
    <Row
      className={`frequency-selector-horizontal ${
        isPerShipment ? 'frequency-selector-horizontal-per-shipment' : ''
      }`}
      style={{
        alignItems: 'flex-start',
        display: 'flex',
        flexWrap: 'nowrap',
      }}
    >
      <Col
        className='frequency-selector-frequency-col'
        span={
          selectedFrequency == null || selectedFrequency === 'REALTIME' ? 24 : 7
        }
        style={{ marginRight: '16px' }}
      >
        <Form.Item
          className='custom-input label-14px'
          label='Frequency'
          name='frequency'
          rules={[{ required: true, message: 'Please select frequency' }]}
        >
          <CustomSelect
            // suffixIcon={<DownOutlined style={{ color: "var(--text-secondary)" }} />}
            placeholder='Select'
            onChange={(value) => setSelectedFrequency(value)}
            options={getFrequencyOptions(isPerShipment)}
          />
        </Form.Item>
      </Col>

      {(selectedFrequency === 'ONCE_A_DAY' ||
        selectedFrequency === 'TWICE_A_DAY' ||
        selectedFrequency === 'WEEKLY') && (
        <Col className='frequency-selector-send-group-col' span={16}>
          <span className='frequency-selector-send-label'>
            {selectedFrequency === 'WEEKLY' ? 'Send On' : 'Send at'}
            <span style={{ color: 'var(--danger)', marginLeft: '3px' }}>*</span>
          </span>

          <div className='frequency-selector-send-controls'>
            {selectedFrequency === 'WEEKLY' && (
              <div className='frequency-selector-day-col'>
                <Form.Item
                  className='frequency-selector-day-item'
                  name={isPerShipment ? 'day' : 'day_of_week'}
                  rules={[{ required: true, message: 'Please select a day' }]}
                >
                  <CustomSelect
                    suffixIcon={
                      <DownOutlined
                        style={{ color: 'var(--text-secondary)' }}
                      />
                    }
                    placeholder='Select Day'
                    options={dayOptions}
                  />
                </Form.Item>
              </div>
            )}

            {selectedFrequency === 'WEEKLY' && (
              <div className='frequency-selector-separator-col'>
                <Text className='frequency-selector-separator'>at</Text>
              </div>
            )}

            <div className='frequency-selector-time-col'>
              <Form.Item
                className='frequency-selector-time-item'
                name={
                  selectedFrequency === 'WEEKLY'
                    ? isPerShipment
                      ? 'time_2'
                      : 'send_at_2'
                    : isPerShipment
                      ? 'time_1'
                      : 'send_at_1'
                }
                rules={[{ required: true, message: 'Please select a time' }]}
              >
                <TimePicker
                  style={{
                    border: 'none',
                    height: '36px',
                    background: 'var(--light-gray)',
                    borderRadius: '8px',
                    width: '100%',
                  }}
                  suffixIcon={
                    <DownOutlined
                      style={{
                        color: 'var(--text-secondary)',
                        fontSize: '12px',
                      }}
                    />
                  }
                  use12Hours
                  format='h:mm a'
                />
              </Form.Item>
            </div>

            {selectedFrequency === 'TWICE_A_DAY' && (
              <>
                <div className='frequency-selector-separator-col'>
                  <Text className='frequency-selector-separator'>&</Text>
                </div>
                <div className='frequency-selector-time-col'>
                  <Form.Item
                    className='frequency-selector-time-item'
                    name={isPerShipment ? 'time_2' : 'send_at_2'}
                    rules={[
                      { required: true, message: 'Please select a time' },
                    ]}
                  >
                    <TimePicker
                      style={{
                        border: 'none',
                        height: '36px',
                        background: 'var(--light-gray)',
                        borderRadius: '8px',
                        width: '100%',
                      }}
                      suffixIcon={
                        <DownOutlined
                          style={{
                            color: 'var(--text-secondary)',
                            fontSize: '12px',
                          }}
                        />
                      }
                      use12Hours
                      format='h:mm a'
                    />
                  </Form.Item>
                </div>
              </>
            )}
          </div>

          <Text className='frequency-selector-timezone'>
            {getFormattedUserTimeZone()}
          </Text>
        </Col>
      )}
    </Row>
  );
};

export default FrequencySelectorHorizontal;
